import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Briefcase, Info, Loader2, RefreshCw, Search, Shuffle, UserPlus, Save } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import {
  agruparClientes, distribuir, normalizarDocumento, resumoPorPessoa,
  type ClienteCarteira, type FaturaDaCarteira, type ModoDistribuicao,
} from '../../src/lib/faturamento/carteiraFaturamento';

// Carteira de faturamento (#6242). Mostra a divisão atual por pessoa, gera uma prévia
// equilibrada pelo peso de dificuldade e só grava quando adm ou gerente confirma.

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const mesRef = (d: Date) => `${MESES[d.getMonth()]}/${d.getFullYear()}`;
const mesesRecentes = (n: number) => {
  const hoje = new Date();
  return Array.from({ length: n }, (_, i) => mesRef(new Date(hoje.getFullYear(), hoje.getMonth() - i, 1)));
};
const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface Pessoa { id: string; full_name: string; role: string }

/** Departamento do cadastro (Configurações › Usuários) de quem faz parte da divisão. */
export const DEPARTAMENTO_FATURAMENTO = 'Faturamento';

export const ehDoFaturamento = (p?: { departamento?: string | null; departamentos?: string[] | null } | null) =>
  !!p && (p.departamento === DEPARTAMENTO_FATURAMENTO || !!p.departamentos?.includes(DEPARTAMENTO_FATURAMENTO));

async function buscarTodas<T>(montar: (de: number, ate: number) => PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  const todas: T[] = [];
  for (let de = 0; ; de += 1000) {
    const { data, error } = await montar(de, de + 999);
    if (error) throw error;
    todas.push(...(data || []));
    if (!data || data.length < 1000) return todas;
  }
}

const CarteiraFaturamentoView: React.FC = () => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const podeDistribuir = ['adm', 'gerente'].includes(profile?.role || '');

  const opcoesMes = useMemo(() => mesesRecentes(4), []);
  const [mes, setMes] = useState(opcoesMes[1]); // o último ciclo fechado
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [clientes, setClientes] = useState<ClienteCarteira[]>([]);
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [equipe, setEquipe] = useState<string[]>([]);
  const [atual, setAtual] = useState<Record<string, string>>({});
  const [previa, setPrevia] = useState<Record<string, string> | null>(null);
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    setPrevia(null);
    try {
      const janelaRisco = mesesRecentes(3);
      const [faturas, vencidas, { data: perfis }, carteira] = await Promise.all([
        buscarTodas<FaturaDaCarteira>((de, ate) => supabase.from('faturas')
          .select('cliente_cpf_cnpj, cliente_nome, classificacao, tarifa_parc_injet_s_desc_gd2, energia_injetada_gd2_kwh, valor_boleto_vigor')
          .eq('mes_referencia', mes).is('excluido_em', null).neq('ambiente', 'sandbox').range(de, ate)),
        buscarTodas<{ cliente_cpf_cnpj: string | null }>((de, ate) => supabase.from('faturas')
          .select('cliente_cpf_cnpj').eq('status_boleto_vigor', 'Vencido').in('mes_referencia', janelaRisco).is('excluido_em', null).range(de, ate)),
        // Só o time de faturamento entra na divisão: departamento "Faturamento", ativo.
        supabase.from('profiles').select('id, full_name, role')
          .or(`departamento.eq.${DEPARTAMENTO_FATURAMENTO},departamentos.cs.{${DEPARTAMENTO_FATURAMENTO}}`)
          .eq('status', 'active').order('full_name'),
        buscarTodas<{ cpf_cnpj_cliente: string; responsavel_id: string }>((de, ate) => supabase.from('carteira_faturamento')
          .select('cpf_cnpj_cliente, responsavel_id').range(de, ate)),
      ]);
      const emRisco = new Set(vencidas.map(v => normalizarDocumento(v.cliente_cpf_cnpj)).filter(Boolean));
      setClientes(agruparClientes(faturas, emRisco));
      const lista = (perfis || []) as Pessoa[];
      setPessoas(lista);
      const mapaAtual = Object.fromEntries(carteira.map(c => [c.cpf_cnpj_cliente, c.responsavel_id]));
      setAtual(mapaAtual);
      setEquipe(lista.map(p => p.id));
    } catch (err: any) {
      addToast('Não consegui carregar a carteira: ' + (err?.message || 'erro desconhecido'), 'error');
    } finally {
      setLoading(false);
    }
  }, [mes, addToast]);

  useEffect(() => { carregar(); }, [carregar]);

  const mapa = previa ?? atual;
  const resumo = useMemo(() => resumoPorPessoa(clientes, mapa), [clientes, mapa]);
  const nomeDe = (id?: string) => pessoas.find(p => p.id === id)?.full_name || '—';
  const semDono = clientes.filter(c => !mapa[c.cpf]).length;
  const linhasResumo = [...new Set([...equipe, ...Object.keys(resumo)])];
  const totalPeso = clientes.reduce((s, c) => s + c.peso, 0);

  const gerarPrevia = (modo: ModoDistribuicao) => {
    if (equipe.length === 0) { addToast('Escolha pelo menos uma pessoa do time.', 'warning'); return; }
    setPrevia(distribuir(clientes, equipe, atual, modo));
  };

  const trocarDono = (cpf: string, id: string) => setPrevia({ ...(previa ?? atual), [cpf]: id });

  const mudancas = previa ? clientes.filter(c => previa[c.cpf] && previa[c.cpf] !== atual[c.cpf]) : [];

  const confirmar = async () => {
    if (!previa || mudancas.length === 0) return;
    setSalvando(true);
    try {
      const por = profile?.full_name || 'Sistema';
      const linhas = mudancas.map(c => ({
        cpf_cnpj_cliente: c.cpf, cliente_nome: c.nome || null, responsavel_id: previa[c.cpf],
        atribuido_em: new Date().toISOString(), atribuido_por: por,
      }));
      for (let i = 0; i < linhas.length; i += 500) {
        const { error } = await supabase.from('carteira_faturamento').upsert(linhas.slice(i, i + 500), { onConflict: 'cpf_cnpj_cliente' });
        if (error) throw error;
      }
      addToast(`Carteira salva: ${mudancas.length} cliente(s) com dono novo.`, 'success');
      await carregar();
    } catch (err: any) {
      addToast('Não consegui salvar a carteira: ' + (err?.message || 'erro desconhecido'), 'error');
    } finally {
      setSalvando(false);
    }
  };

  const clientesFiltrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    const base = [...clientes].sort((a, b) => b.peso - a.peso || b.valor - a.valor);
    if (!t) return base.slice(0, 200);
    const dig = t.replace(/\D/g, '');
    return base.filter(c => c.nome.toLowerCase().includes(t) || (dig && c.cpf.includes(dig)) || nomeDe(mapa[c.cpf]).toLowerCase().includes(t)).slice(0, 200);
  }, [busca, clientes, mapa, pessoas]);

  return (
    <div className="animate-fade-in space-y-6 pb-24">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-poppins font-bold dark:text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center shadow-lg shadow-primary/20">
              <Briefcase size={20} className="text-white" />
            </div>
            Carteira de faturamento
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-[52px] max-w-3xl">
            Cada cliente tem uma pessoa do faturamento responsável por todas as faturas dele. A divisão equilibra a dificuldade das faturas entre o time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-500 dark:text-gray-400" htmlFor="mes-carteira">Ciclo</label>
          <select
            id="mes-carteira"
            value={mes}
            onChange={e => setMes(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm dark:text-white"
          >
            {opcoesMes.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <button onClick={carregar} disabled={loading} className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-primary disabled:opacity-50" title="Recarregar">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3">
        <Info size={14} className="mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />
        <p>
          Peso de cada fatura: <strong className="text-gray-700 dark:text-gray-200">1</strong> fatura B comum ou avulsa,{' '}
          <strong className="text-gray-700 dark:text-gray-200">2</strong> GD2,{' '}
          <strong className="text-gray-700 dark:text-gray-200">3</strong> grupo A, tarifa branca ou A optante B. Cliente com fatura vencida nos últimos 3 meses soma{' '}
          <strong className="text-gray-700 dark:text-gray-200">+1</strong>. Os pesos são calculados com as faturas de {mes}; a carteira vale para todos os ciclos.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-gray-500"><Loader2 className="animate-spin" size={18} /> Carregando carteira...</div>
      ) : (
        <>
          {/* Time */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="font-bold text-gray-800 dark:text-white flex items-center gap-2"><UserPlus size={16} /> Time do faturamento</h3>
              {podeDistribuir && (
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => gerarPrevia('manter')} className="px-3 py-2 rounded-lg text-sm font-semibold bg-primary/10 text-primary hover:bg-primary/20">
                    Distribuir clientes sem dono
                  </button>
                  <button onClick={() => gerarPrevia('redividir')} className="px-3 py-2 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary/50 flex items-center gap-1.5">
                    <Shuffle size={14} /> Redividir tudo
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {pessoas.map(p => {
                const no = equipe.includes(p.id);
                return (
                  <button
                    key={p.id}
                    disabled={!podeDistribuir}
                    aria-pressed={no}
                    onClick={() => setEquipe(prev => no ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors disabled:cursor-default ${no
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-primary/50'}`}
                  >
                    {p.full_name}
                  </button>
                );
              })}
            </div>
            {!podeDistribuir && <p className="text-xs text-gray-400 mt-3">Só adm e gerente podem distribuir a carteira.</p>}
          </div>

          {/* Resumo por pessoa */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-bold text-gray-800 dark:text-white">
                {previa ? 'Prévia da divisão (ainda não salva)' : 'Divisão atual'}
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {clientes.length} clientes · peso total {totalPeso} · {semDono} sem dono
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-gray-400 bg-gray-50 dark:bg-gray-900/40">
                  <tr>
                    <th className="text-left px-4 py-2">Pessoa</th>
                    <th className="text-right px-4 py-2">Clientes</th>
                    <th className="text-right px-4 py-2">Faturas</th>
                    <th className="text-right px-4 py-2">Peso</th>
                    <th className="text-right px-4 py-2">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {linhasResumo.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Ninguém com carteira ainda. Escolha o time e clique em "Distribuir clientes sem dono".</td></tr>
                  )}
                  {linhasResumo.map(id => {
                    const r = resumo[id] ?? { clientes: 0, faturas: 0, peso: 0, valor: 0 };
                    return (
                      <tr key={id} className="border-t border-gray-100 dark:border-gray-700/60">
                        <td className="px-4 py-2 font-semibold text-gray-800 dark:text-gray-100">
                          {nomeDe(id)}{!equipe.includes(id) && <span className="ml-2 text-[10px] font-bold text-amber-600">fora do time</span>}
                        </td>
                        <td className="px-4 py-2 text-right dark:text-gray-200">{r.clientes}</td>
                        <td className="px-4 py-2 text-right dark:text-gray-200">{r.faturas}</td>
                        <td className="px-4 py-2 text-right font-bold dark:text-white">{r.peso}</td>
                        <td className="px-4 py-2 text-right dark:text-gray-200">{brl(r.valor)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {previa && podeDistribuir && (
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-end gap-2 bg-amber-50/60 dark:bg-amber-900/10">
                <span className="text-xs text-amber-800 dark:text-amber-300 mr-auto">{mudancas.length} cliente(s) mudam de dono ao salvar.</span>
                <button onClick={() => setPrevia(null)} className="px-3 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">Descartar</button>
                <button onClick={confirmar} disabled={salvando || mudancas.length === 0} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-60 flex items-center gap-1.5">
                  {salvando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar carteira
                </button>
              </div>
            )}
          </div>

          {/* Clientes */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-3">
              <h3 className="font-bold text-gray-800 dark:text-white mr-auto">Clientes</h3>
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                  placeholder="Buscar cliente, CPF/CNPJ ou pessoa..."
                  className="w-full pl-9 pr-3 py-2 rounded-full text-sm bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 dark:text-white outline-none focus:border-primary"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-gray-400 bg-gray-50 dark:bg-gray-900/40">
                  <tr>
                    <th className="text-left px-4 py-2">Cliente</th>
                    <th className="text-right px-4 py-2">Faturas</th>
                    <th className="text-right px-4 py-2">Peso</th>
                    <th className="text-right px-4 py-2">Valor</th>
                    <th className="text-left px-4 py-2">Responsável</th>
                  </tr>
                </thead>
                <tbody>
                  {clientesFiltrados.map(c => (
                    <tr key={c.cpf} className="border-t border-gray-100 dark:border-gray-700/60">
                      <td className="px-4 py-2">
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{c.nome || 'Sem nome'}</p>
                        <p className="text-xs text-gray-400">{c.cpf}{c.risco && <span className="ml-2 font-bold text-red-600">vencida nos últimos 3 meses</span>}</p>
                      </td>
                      <td className="px-4 py-2 text-right dark:text-gray-200">{c.faturas}</td>
                      <td className="px-4 py-2 text-right font-bold dark:text-white">{c.peso}</td>
                      <td className="px-4 py-2 text-right dark:text-gray-200">{brl(c.valor)}</td>
                      <td className="px-4 py-2">
                        {podeDistribuir ? (
                          <select
                            value={mapa[c.cpf] || ''}
                            onChange={e => trocarDono(c.cpf, e.target.value)}
                            aria-label={`Responsável por ${c.nome || c.cpf}`}
                            className={`px-2 py-1 rounded-lg border text-sm bg-white dark:bg-gray-900 dark:text-white ${previa && previa[c.cpf] !== atual[c.cpf] ? 'border-amber-400' : 'border-gray-200 dark:border-gray-700'}`}
                          >
                            <option value="" disabled>Sem dono</option>
                            {pessoas.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                          </select>
                        ) : (
                          <span className="dark:text-gray-200">{mapa[c.cpf] ? nomeDe(mapa[c.cpf]) : 'Sem dono'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {clientes.length > clientesFiltrados.length && (
              <p className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100 dark:border-gray-700">Mostrando {clientesFiltrados.length} de {clientes.length}, dos mais pesados para os mais leves. Use a busca para achar os outros.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CarteiraFaturamentoView;
